/**
 * Symfony FormView JSON builders, matching the h6bridge FormViewNormalizer.
 * ngxh6's sf-form dereferences vars.attr, vars.row_attr, vars.block_prefixes,
 * vars.errors and every choice's attr without guards, so every node built
 * here carries them.
 */

export interface FormNode {
    vars: Record<string, any>;
    children: Record<string, FormNode>;
}

export interface Choice {
    label: string;
    value: string;
    data: string;
    attr: Record<string, unknown>;
    labelTranslationParameters: unknown[];
}

export const choice = (value: string, label: string = value): Choice => ({ label, value, data: value, attr: {}, labelTranslationParameters: [] });

function titleCase(name: string): string {
    const spaced = name.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * One child node. `prefixes` are the widget prefixes between 'form' and the
 * unique `_form_name` prefix, e.g. ['text', 'email'] or ['choice'].
 */
export function field(form: string, name: string, prefixes: string[], extra: Record<string, any> = {}): FormNode {
    const { label, value, required, disabled, help, attr, ...rest } = extra;
    return {
        vars: {
            id: `${form}_${name}`,
            name,
            full_name: `${form}[${name}]`,
            label: label ?? titleCase(name),
            value: value ?? null,
            required: !!required,
            disabled: !!disabled,
            help: help ?? null,
            attr: attr ?? {},
            row_attr: {},
            label_attr: {},
            errors: [],
            valid: true,
            compound: false,
            block_prefixes: ['form', ...prefixes, `_${form}_${name}`],
            ...rest,
        },
        children: {},
    };
}

export const textField = (form: string, name: string, extra: Record<string, any> = {}) => field(form, name, ['text'], extra);
export const emailField = (form: string, name: string, extra: Record<string, any> = {}) => field(form, name, ['text', 'email'], extra);
export const passwordField = (form: string, name: string, extra: Record<string, any> = {}) => field(form, name, ['text', 'password'], { value: '', ...extra });
export const textareaField = (form: string, name: string, extra: Record<string, any> = {}) => field(form, name, ['textarea'], extra);
export const hiddenField = (form: string, name: string, extra: Record<string, any> = {}) => field(form, name, ['hidden'], extra);

export function choiceField(form: string, name: string, choices: Choice[], extra: Record<string, any> = {}): FormNode {
    const { prefixes, ...rest } = extra;
    const multiple = !!rest['multiple'];
    return field(form, name, prefixes ?? ['choice'], {
        multiple,
        expanded: false,
        placeholder: null,
        preferred_choices: [],
        choices,
        value: multiple ? [] : null,
        ...rest,
    });
}

export function lightswitchField(form: string, name: string, checked: boolean, extra: Record<string, any> = {}): FormNode {
    return field(form, name, ['checkbox', 'lightswitch'], { value: '1', checked, ...extra });
}

/** The root node. Children render in insertion order; the model key is the child key. */
export function formView(form: string, children: Record<string, FormNode>, label = ''): FormNode {
    const value: Record<string, unknown> = {};
    for (const [k, c] of Object.entries(children)) {
        value[k] = c.vars['checked'] !== undefined ? c.vars['checked'] : c.vars['value'];
    }
    return {
        vars: {
            id: form,
            name: form,
            full_name: form,
            label: label || titleCase(form),
            value,
            attr: {},
            row_attr: {},
            label_attr: {},
            errors: [],
            help: null,
            required: true,
            disabled: false,
            compound: true,
            valid: true,
            submitted: false,
            block_prefixes: ['form', form],
        },
        children,
    };
}

/** Copy of a FormView with an error attached to one child (or the root), for 400 responses. */
export function withError(view: FormNode, child: string | null, message: string): FormNode {
    const copy: FormNode = JSON.parse(JSON.stringify(view));
    const node = child && copy.children[child] ? copy.children[child] : copy;
    node.vars['errors'] = [...(node.vars['errors'] || []), message];
    node.vars['valid'] = false;
    copy.vars['valid'] = false;
    copy.vars['submitted'] = true;
    return copy;
}
